import { Box, Grid, Stack, Typography, Chip, IconButton } from "@mui/material";
import { LinkedIn as LinkedInIcon, Email as EmailIcon } from "@mui/icons-material";
import { footerColumns } from "../data/data.js";

const SDG_COLORS = [
  "#E5243B","#DDA63A","#4C9F38","#C5192D","#FF3A21",
  "#26BDE2","#FCC30B","#A21942","#FD6925","#DD1367",
  "#FD9D24","#BF8B2E","#3F7E44","#0A97D9","#56C02B",
  "#00689D","#19486A",
];

export default function Footer() {
  return (
    <Box sx={{ bgcolor: "#060f0a" }}>

      {/* SDG colour strip at top */}
      <Box sx={{ display: "flex", height: "4px" }}>
        {SDG_COLORS.map((c, i) => <Box key={i} sx={{ flex: 1, bgcolor: c }} />)}
      </Box>

      <Box sx={{ py: { xs: 6, md: 8 }, px: { xs: 3, md: 8 } }}>
        <Grid
          container spacing={4}
          sx={{ pb: 5, borderBottom: "1px solid rgba(255,255,255,0.07)" }}
        >

          {/* ── Brand column ── */}
          <Grid size={{ xs: 12, md: 4 }}>

            {/* Logo + name row */}
            <Stack direction="row" alignItems="center" spacing={2} mb={2.5}>

              {/* Logo with spinning SDG ring */}
              <Box sx={{ position: "relative", width: 52, height: 52, flexShrink: 0 }}>
                {/* Spinning SDG conic ring */}
                <Box
                  sx={{
                    position: "absolute", inset: -2, borderRadius: "50%",
                    background: `conic-gradient(${SDG_COLORS.map((c, i) =>
                      `${c} ${(i / SDG_COLORS.length) * 100}% ${((i + 1) / SDG_COLORS.length) * 100}%`
                    ).join(", ")})`,
                    "@keyframes spin": {
                      from: { transform: "rotate(0deg)" },
                      to:   { transform: "rotate(360deg)" },
                    },
                    animation: "spin 20s linear infinite",
                    opacity: 0.85,
                  }}
                />
                {/* Dark mask */}
                <Box sx={{ position: "absolute", inset: 1.5, borderRadius: "50%", bgcolor: "#060f0a" }} />
                {/* Actual logo */}
                <Box
                  component="img"
                  src="/Picture1.png"
                  alt="BTSA Logo"
                  sx={{
                    position: "absolute", inset: 1.5,
                    width: "calc(100% - 3px)", height: "calc(100% - 3px)",
                    borderRadius: "50%", objectFit: "cover", zIndex: 2,
                  }}
                />
              </Box>

              {/* Org name + tagline */}
              <Box>
                <Typography
                  sx={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: "15px", fontWeight: 700,
                    color: "white", lineHeight: 1.2,
                  }}
                >
                  Build To Shelter Africa
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "'Space Mono', monospace",
                    fontSize: "9px", color: "#DDA63A",
                    letterSpacing: "2px", textTransform: "uppercase", mt: 0.4,
                  }}
                >
                  Pan-African NGO · Est. 2025
                </Typography>
              </Box>
            </Stack>

            <Typography
              sx={{
                color: "rgba(255,255,255,0.45)", fontSize: "13px",
                lineHeight: 1.8, maxWidth: 300, mb: 2.5,
              }}
            >
              A youth-led Pan-African non-profit building sustainable shelters and empowering
              communities through innovation, clean energy, and education.
            </Typography>

            <Chip
              label="🟡  Registration in process · Non-profit"
              size="small"
              sx={{
                mb: 3, bgcolor: "rgba(45,156,90,0.12)", color: "#2d9c5a",
                border: "1px solid rgba(45,156,90,0.25)", fontSize: "11px", fontWeight: 600,
              }}
            />

            {/* Social icons */}
            <Stack direction="row" spacing={1}>
              <IconButton
                href="https://www.linkedin.com/company/build-to-shelter-africa/"
                target="_blank"
                size="small"
                sx={{
                  bgcolor: "rgba(255,255,255,0.07)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  color: "rgba(255,255,255,0.6)", borderRadius: 1.5,
                  "&:hover": { bgcolor: "rgba(45,156,90,0.2)", color: "#2d9c5a", borderColor: "rgba(45,156,90,0.4)" },
                }}
              >
                <LinkedInIcon fontSize="small" />
              </IconButton>
              <IconButton
                href="mailto:africabuildtoshelter@gmail.com"
                size="small"
                sx={{
                  bgcolor: "rgba(255,255,255,0.07)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  color: "rgba(255,255,255,0.6)", borderRadius: 1.5,
                  "&:hover": { bgcolor: "rgba(45,156,90,0.2)", color: "#2d9c5a", borderColor: "rgba(45,156,90,0.4)" },
                }}
              >
                <EmailIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Grid>

          {/* ── Link columns ── */}
          {footerColumns.map((col) => (
            <Grid size={{ xs: 6, md: 2 }} key={col.title}>
              <Typography
                sx={{
                  color: "white", fontSize: "11px", fontWeight: 800,
                  letterSpacing: "2px", textTransform: "uppercase", mb: 2.5,
                  fontFamily: "'Space Mono', monospace",
                }}
              >
                {col.title}
              </Typography>
              <Stack spacing={1.4}>
                {col.links.map((link) => (
                  <Typography
                    key={link}
                    component="a"
                    href="#"
                    sx={{
                      color: "rgba(255,255,255,0.4)", fontSize: "13px",
                      textDecoration: "none", display: "block",
                      transition: "color 0.2s",
                      "&:hover": { color: "#2d9c5a" },
                    }}
                  >
                    {link}
                  </Typography>
                ))}
              </Stack>
            </Grid>
          ))}

          {/* ── Contact column ── */}
          <Grid size={{ xs: 12, md: 2 }}>
            <Typography
              sx={{
                color: "white", fontSize: "11px", fontWeight: 800,
                letterSpacing: "2px", textTransform: "uppercase", mb: 2.5,
                fontFamily: "'Space Mono', monospace",
              }}
            >
              Contact
            </Typography>
            <Stack spacing={1.8}>
              {[
                { label: "Email", value: "africabuildtoshelter@gmail.com" },
                { label: "Phone", value: "+256 761 070 710" },
                { label: "Office", value: "Kampala, Uganda" },
              ].map((item) => (
                <Box key={item.label}>
                  <Typography sx={{ color: "rgba(255,255,255,0.3)", fontSize: "10px", fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", mb: 0.3 }}>
                    {item.label}
                  </Typography>
                  <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: "12px", lineHeight: 1.5 }}>
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Grid>
        </Grid>

        {/* ── Bottom bar ── */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ sm: "center" }}
          sx={{ pt: 3 }}
          spacing={2}
        >
          <Typography sx={{ color: "rgba(255,255,255,0.25)", fontSize: "12px" }}>
            © 2025 Build To Shelter Africa (BTSA). All rights reserved.
          </Typography>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography sx={{ color: "rgba(255,255,255,0.25)", fontSize: "11px" }}>
              Aligned with
            </Typography>
            <Chip
              label="UN SDGs"
              size="small"
              sx={{ bgcolor: "rgba(26,107,60,0.25)", color: "#2d9c5a", fontWeight: 700, fontSize: "10px" }}
            />
            <Chip
              label="Agenda 2063"
              size="small"
              sx={{ bgcolor: "rgba(232,93,4,0.2)", color: "#f48c06", fontWeight: 700, fontSize: "10px" }}
            />
          </Stack>
        </Stack>
      </Box>

      {/* SDG colour strip at bottom */}
      <Box sx={{ display: "flex", height: "4px" }}>
        {SDG_COLORS.map((c, i) => <Box key={i} sx={{ flex: 1, bgcolor: c }} />)}
      </Box>

    </Box>
  );
}
